import { useMemo } from "react";
import { t } from "ttag";

import SidebarContent from "metabase/query_builder/components/SidebarContent";
import {
  ChartTypeSettings,
  getSensibleVisualizations,
  useQuestionVisualizationState,
} from "metabase/query_builder/components/chart-type-selector";
import { Box } from "metabase/ui";
import visualizations from "metabase/visualizations";
import { QuestionChartSettings } from "metabase/visualizations/components/ChartSettings";
import type Question from "metabase-lib/v1/Question";
import type { Dataset, VisualizationSettings } from "metabase-types/api";

import type { AdhocResult } from "../types";

export type ResultSidebarPanel = "chart-type" | "chart-settings";

export type ResultSidebarProps = {
  panel: ResultSidebarPanel | null;
  result: AdhocResult | undefined;
  question: Question;
  onSelectVisualization: (question: Question) => void;
  onChangeSettings: (settings: VisualizationSettings) => void;
  onChangePanel: (panel: ResultSidebarPanel | null) => void;
};

/**
 * Chart type / chart settings sidebar for one result.
 *
 * The query builder's own sidebars dispatch into its single-question store, so
 * this composes the same prop-driven panels they wrap.
 */
export function ResultSidebar({
  panel,
  result,
  question,
  onSelectVisualization,
  onChangeSettings,
  onChangePanel,
}: ResultSidebarProps) {
  const dataset = result?.dataset;
  if (panel == null || result == null || dataset?.data == null) {
    return null;
  }

  return (
    <Box h="100%" data-testid="adhoc-result-sidebar">
      {panel === "chart-type" ? (
        <ChartTypePanel
          dataset={dataset}
          question={question}
          onSelectVisualization={onSelectVisualization}
          onClose={() => onChangePanel(null)}
          onOpenSettings={() => onChangePanel("chart-settings")}
        />
      ) : (
        <ChartSettingsPanel
          dataset={dataset}
          question={question}
          onChangeSettings={onChangeSettings}
          onBack={() => onChangePanel("chart-type")}
          onClose={() => onChangePanel(null)}
        />
      )}
    </Box>
  );
}

function ChartTypePanel({
  dataset,
  question,
  onSelectVisualization,
  onClose,
  onOpenSettings,
}: {
  dataset: Dataset;
  question: Question;
  onSelectVisualization: (question: Question) => void;
  onClose: () => void;
  onOpenSettings: () => void;
}) {
  const { sensibleVisualizations, nonSensibleVisualizations } = useMemo(
    () => getSensibleVisualizations({ result: dataset }),
    [dataset],
  );

  const { selectedVisualization, updateQuestionVisualization } =
    useQuestionVisualizationState({
      question,
      onUpdateQuestion: onSelectVisualization,
    });

  return (
    <SidebarContent
      title={t`Visualization`}
      onClose={onClose}
      data-testid="adhoc-chart-type-sidebar"
    >
      <ChartTypeSettings
        px="xl"
        selectedVisualization={selectedVisualization}
        onSelectVisualization={updateQuestionVisualization}
        onOpenSettings={onOpenSettings}
        sensibleVisualizations={sensibleVisualizations}
        nonSensibleVisualizations={nonSensibleVisualizations}
      />
    </SidebarContent>
  );
}

function ChartSettingsPanel({
  dataset,
  question,
  onChangeSettings,
  onBack,
  onClose,
}: {
  dataset: Dataset;
  question: Question;
  onChangeSettings: (settings: VisualizationSettings) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const series = useMemo(
    () => [{ ...dataset, card: question.card() }],
    [dataset, question],
  );

  return (
    <SidebarContent
      title={t`${visualizations.get(question.display())?.getUiName()} options`}
      onBack={onBack}
      onClose={onClose}
      data-testid="adhoc-chart-settings-sidebar"
    >
      <QuestionChartSettings
        question={question}
        series={series}
        onChange={onChangeSettings}
      />
    </SidebarContent>
  );
}
